import { v4 as uuidv4 } from "uuid";
import { notify } from "../api/photoshop";
import { photoshop } from "../globals";
import { useListenFromRenderer } from "../message";
import { postToRenderer } from "../message/utils";
import { base64ToUint8Array } from "../utils/jimp";
import { isCropToolActive } from "./shared/check-crop-tool";
import { checkPromptSafety } from "./shared/check-prompt-safety";
import { fetchInferenceJob } from "./shared/fetch-inference-job";

const callNanoBananaGeneration = async (
	prompt: string,
	aspect_ratio: string,
	resolution: string,
	model: string,
	input_images: string[],
	messageId: string,
	jwt: string,
	tool_document_id: string,
	batch_size: number,
) => {
	let success = false;
	if (isCropToolActive()) {
		await notify("Cannot generate images while the Crop tool is active.");
		return success;
	}
	await photoshop.core.executeAsModal(
		async (executionContext) => {
			try {
				const promptSafetyResult = checkPromptSafety(prompt);
				if (promptSafetyResult.detected) {
					await promptSafetyResult.onFailed();
					return;
				}

				const data = {
					prompt: `${prompt}`,
					aspect_ratio,
					resolution,
					model,
					input_images,
					jwt,
					tool_document_id,
					batch_size,
				};

				const result = await fetchNanoBananaGeneration(
					data,
					executionContext,
					messageId,
				);

				if (!result) {
					return;
				}

				const resultImages = Array.isArray(result) ? result : [result];
				const firstDecoded = base64ToUint8Array(resultImages[0]);

				const newDoc = await photoshop.app.createDocument({
					width: firstDecoded.width,
					height: firstDecoded.height,
					name: `Nano Banana - ${Date.now()}`,
				});
				if (!newDoc) {
					await notify("Failed to create document");
					return;
				}

				const createdLayers: any[] = [];

				for (let i = 0; i < resultImages.length; i++) {
					const decoded = i === 0 ? firstDecoded : base64ToUint8Array(resultImages[i]);
					const uint8Array = new Uint8Array(decoded.rawPixels);
					const resultImageData =
						await photoshop.imaging.createImageDataFromBuffer(uint8Array, {
							width: decoded.width,
							height: decoded.height,
							components: 4,
							colorSpace: "RGB",
						});

					const layerName = resultImages.length > 1
						? `Nano Banana - Image Generation ${i + 1}`
						: "Nano Banana - Image Generation";

					const newLayer = await newDoc.createPixelLayer({
						name: layerName,
					});
					if (!newLayer) {
						resultImageData.dispose();
						continue;
					}
					await photoshop.imaging.putPixels({
						documentID: newDoc.id,
						layerID: newLayer.id,
						imageData: resultImageData,
						targetBounds: {
							left: 0,
							top: 0,
							right: decoded.width,
							bottom: decoded.height,
						},
						replace: true,
					});
					createdLayers.push(newLayer);
					await resultImageData.dispose();
				}

				if (createdLayers.length > 1) {
					for (const layer of createdLayers) {
						layer.selected = true;
					}
				}

				success = true;
				postToRenderer([
					{
						key: "documentInfoChanged",
						id: uuidv4(),
						values: {
							width: newDoc.width,
							height: newDoc.height,
						},
					},
				]);
			} catch (err) {
				console.error(err);
				await notify(`${err}`);
			}
		},
		{
			commandName: "Nano Banana - Image Generation",
		},
	);
	return success;
};

export async function fetchNanoBananaGeneration(
	params: {
		prompt: string;
		aspect_ratio: string;
		input_images: string[];
		resolution: string;
		model: string;
		batch_size: number;
	},
	executionContext: any,
	messageId: string,
): Promise<string | string[] | false> {
	return fetchInferenceJob({
		params,
		executionContext,
		messageId,
		apiPath: "/infer/gemini",
		statusEndpoint: (apiUrl, jobId) =>
			`${apiUrl.replace("/infer/gemini", "")}/status/${jobId}`,
		resultEndpoint: (apiUrl, jobId) =>
			`${apiUrl.replace("/infer/gemini", "")}/result/${jobId}`,
		cancelEndpoint: (apiUrl, jobId) => ({
			url: `${apiUrl.replace("/infer/gemini", "")}/cancel/${jobId}`,
			method: "POST",
		}),
		extractResult: (data) => data.image_base64_list ?? [data.image_base64],
		useGatewayUrl: true,
	});
}

export function useListenNanoBananaGenerationMessage() {
	useListenFromRenderer([
		{
			key: "nanoBananaGeneration",
			onMessage: async (message) => {
				const success = await callNanoBananaGeneration(
					message.values.prompt,
					message.values.aspect_ratio,
					message.values.resolution,
					message.values.model,
					message.values.input_images,
					message.id,
					message.values.jwt,
					message.values.tool_document_id,
					message.values.batch_size,
				);
				postToRenderer([
					{
						key: "finish",
						id: uuidv4(),
						values: {
							id: message.id,
							status: success ? "success" : "error",
						},
					},
				]);
			},
		},
	]);
}
